import { Box, Button, Checkbox, FormControlLabel, Grid, MenuItem, Modal, Select, TextField } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import LoadingButton from '@mui/lab/LoadingButton';

import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { addTag, fetchTagById, updateTag } from '../../store/tagStore/tagThunk';
import { ApiStatus } from '../../utils/api.types';
import { ITagFormInputs, TAG_APPLIES_TO_OPTIONS, TAG_STATUS_OPTIONS } from '../../utils/tag.types';

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  transition: '0ms',
  width: 650,
  borderRadius: 2,
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
  padding: 0
};

// Preset palette, kept identical to the inventory tag picker so a tag looks the
// same wherever it is created.
const TAG_COLORS = ['#488dff', '#49a563', '#f5a623', '#e0566f', '#9b51e0', '#26a0a0', '#7f8c9b', '#d0021b'];
const DEFAULT_COLOR = TAG_COLORS[0];

interface DialogTagFormProps {
  tagId?: string;
  open: boolean;
  onClose: (isSaved: boolean) => void;
}

const DialogTagForm: FC<DialogTagFormProps> = ({ onClose, tagId, open }) => {
  const dispatch = useAppDispatch();
  const { status } = useAppSelector(state => state.tags);

  const [tagData, setTagData] = useState<any>();
  const [selectedColor, setSelectedColor] = useState<string>(DEFAULT_COLOR);
  // Which entities this tag may be attached to. Empty => generic (usable anywhere).
  const [appliesTo, setAppliesTo] = useState<string[]>([]);
  const [tagStatus, setTagStatus] = useState('active');

  // Schema to validate tag form.
  const schema = yup.object().shape({
    name: yup.string().required("Tag name is required.")
  });

  const methods = useForm<ITagFormInputs>({ mode: 'onTouched', resolver: yupResolver(schema) });

  /**
   * Hydrate the form when editing; reset it when creating.
   */
  useEffect(() => {
    const getTagDetails = async () => {
      if (tagId) {
        const res: any = await dispatch(fetchTagById(tagId));
        const tag = res.payload;

        if (tag) {
          setTagData(tag);
          methods.setValue('name', tag.name || '');
          methods.setValue('description', tag.description || '');
          setSelectedColor(tag.color || DEFAULT_COLOR);
          setAppliesTo(tag.appliesTo || []);
          setTagStatus(tag.status || 'active');
        }
      } else {
        setTagData(undefined);
        methods.reset({ name: '', description: '' });
        setSelectedColor(DEFAULT_COLOR);
        setAppliesTo([]);
        setTagStatus('active');
      }
    };

    if (open) {
      getTagDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagId, open, dispatch]);

  /**
   * Toggle one "Applies To" entity type.
   */
  const toggleAppliesTo = (value: string, checked: boolean) => {
    setAppliesTo(prev => (checked ? [...prev, value] : prev.filter(item => item !== value)));
  };

  const onSubmit = async (data: ITagFormInputs) => {
    const tagPayload: ITagFormInputs = {
      name: data.name,
      description: data.description || '',
      color: selectedColor,
      appliesTo,
      status: tagStatus,
    };

    const resData: any = tagId
      ? await dispatch(updateTag({ id: tagId, tagPayload }))
      : await dispatch(addTag(tagPayload));

    // Keep the form open on failure (e.g. duplicate name) so the user can fix it.
    if (!resData.error) {
      methods.reset();
      onClose(true);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => onClose(false)}
    >
      <FormProvider {...methods}>
        {(!tagId || (tagId && tagData)) &&
          <Box sx={style} component="form" onSubmit={methods.handleSubmit(onSubmit)} noValidate>
            <Grid container >
              <Grid {...{ xs: 12, sm: 8, md: 4, lg: 12, p: 2, pl: 2, bgcolor: '#e9ecf1' }} style={{ borderTopLeftRadius: 8, borderTopRightRadius: 8, height: 50 }} >
                <div style={{ float: 'right', marginTop: -7, marginRight: -5 }}>
                  <LoadingButton
                    type="submit"
                    name={'submit'}
                    loading={status === ApiStatus.LOADING}
                    style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                    variant="contained"
                    size='small'>
                    Save
                  </LoadingButton>

                  <Button
                    style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                    variant="contained"
                    size='small'
                    onClick={() => {
                      methods.reset();
                      onClose(false);
                    }} >
                    Cancel
                  </Button>
                </div>
                <h3 style={{ marginTop: -2 }}>{tagId ? 'Tag Update' : 'Add Tag Details'}</h3>
              </Grid>
            </Grid>

            <Grid container p={2} spacing={2}>
              {/* Tag Name */}
              <Grid item xs={6} pr={1}>
                <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Tag Name*</h5>
                <TextField
                  placeholder='Enter Tag Name'
                  required
                  size='small'
                  {...methods.register('name')}
                  className='c-text-field-4'
                  variant="standard"
                  sx={{ width: '100%', borderRadius: '8px!important' }}
                  InputProps={{ disableUnderline: true }}
                />
                <p style={{ color: "#c33b3b" }}>{methods.formState.errors.name?.message ?? ""}</p>
              </Grid>

              {/* Status */}
              <Grid item xs={6} pr={1}>
                <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Status</h5>
                <Select
                  className='c-text-field-4'
                  value={tagStatus}
                  onChange={(e) => setTagStatus(e.target.value)}
                  disableUnderline
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        border: "1.5px solid #e9ecf1",
                        boxShadow: 'none',
                        borderRadius: '5px'
                      }
                    }
                  }}
                  variant='standard'
                  sx={{ width: '100%', boxShadow: "none", borderRadius: '7px !important' }}
                >
                  {TAG_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option} style={{ textTransform: 'capitalize' }}>{option}</MenuItem>
                  ))}
                </Select>
              </Grid>

              {/* Description */}
              <Grid item xs={12} pr={1}>
                <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Description</h5>
                <TextField
                  placeholder='Enter Description'
                  size='small'
                  {...methods.register('description')}
                  className='c-text-field-4'
                  variant="standard"
                  sx={{ width: '100%', borderRadius: '8px!important' }}
                  InputProps={{ disableUnderline: true }}
                />
              </Grid>

              {/* Applies To */}
              <Grid item xs={12} pr={1}>
                <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Applies To</h5>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  {TAG_APPLIES_TO_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      control={
                        <Checkbox
                          size='small'
                          checked={appliesTo.includes(option.value)}
                          onChange={(e) => toggleAppliesTo(option.value, e.target.checked)}
                        />
                      }
                      label={<span style={{ fontSize: 14, color: '#000' }}>{option.label}</span>}
                    />
                  ))}
                </div>
                <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 12, marginTop: 4 }}>
                  Leave both unchecked to make this tag available everywhere.
                </h5>
              </Grid>

              {/* Color */}
              <Grid item xs={12} pr={1}>
                <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Color</h5>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                  {TAG_COLORS.map((color) => (
                    <Box
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: color,
                        cursor: 'pointer',
                        boxShadow: selectedColor === color ? `0 0 0 2px #fff, 0 0 0 4px ${color}` : 'none',
                        transition: '0.1s',
                      }}
                    />
                  ))}
                  {/* Custom color */}
                  <Box sx={{ position: 'relative', width: 24, height: 24, ml: 0.5 }}>
                    <input
                      type="color"
                      value={selectedColor}
                      onChange={(e) => setSelectedColor(e.target.value)}
                      title="Custom color"
                      style={{
                        width: 24,
                        height: 24,
                        padding: 0,
                        border: '1px dashed #9da7b7',
                        borderRadius: '50%',
                        background: 'none',
                        cursor: 'pointer',
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        }
      </FormProvider>
    </Modal>
  );
};

export default DialogTagForm;
